import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { App } from "antd";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import BaseSkeleton from "@/components_v2/Skeleton/BaseSkeleton";
import BaseSkeletonGroup from "@/components_v2/Skeleton/BaseSkeletonGroup";
import { SKELETON_VARIANTS } from "@/components_v2/Skeleton/constants";
import {
  useGetAdminKnowledgeCheckQuery,
  useGetKnowledgeCheckAttemptQuery,
  useGetKnowledgeCheckAttemptsQuery,
  useReviewAttemptMutation,
} from "@/redux/service";
import type { ReviewVerdict } from "@/redux/certifications/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { formatShortCertificationDate } from "@/domains/certifications/utils";
import AnswerReview from "@/features/certifications/knowledge-check-flags/components/AnswerReview";
import {
  REVIEW_BLOCKERS,
  REVIEW_MODAL_WIDTH,
} from "@/features/certifications/knowledge-check-flags/constants";
import {
  buildReviewRequest,
  countAiFlagged,
  createDecidedDraft,
  createDraft,
  getConfirmedScore,
  getReviewBlocker,
  hasAiVerdicts,
  isPreviewedPass,
  orderAnswers,
  sortAttempts,
} from "@/features/certifications/knowledge-check-flags/utils";
import {
  ReviewDraft,
  ReviewModalBodyProps,
} from "@/features/certifications/knowledge-check-flags/types";

import "../styles.css";

const DECIDED_TOAST_KEYS: Partial<Record<string, string>> = {
  PASSED: "certification.admin.knowledgeCheckFlags.approved",
  REJECTED: "certification.admin.knowledgeCheckFlags.returned",
};

const ANSWER_SKELETON_LINES = 6;

const ReviewModalBody = ({
  open,
  attempt,
  name,
  subtitle,
  draft,
  onDraftChange,
  onClose,
  onDecided,
}: ReviewModalBodyProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const hintId = useId();
  const [selectedId, setSelectedId] = useState(attempt.id);
  const [reviewAttempt, { isLoading }] = useReviewAttemptMutation();

  const history = useGetKnowledgeCheckAttemptsQuery({
    enrollment: attempt.enrollment,
    knowledgeCheck: attempt.knowledgeCheck,
    pagination: "off",
  });
  const check = useGetAdminKnowledgeCheckQuery(attempt.knowledgeCheck);
  const pending = useGetKnowledgeCheckAttemptQuery(attempt.id);

  const historyRows = history.data
    ? isPaginatedResponse(history.data)
      ? history.data.results
      : history.data
    : [];
  const attempts = sortAttempts(
    historyRows.some(({ id }) => id === attempt.id)
      ? historyRows
      : [...historyRows, attempt],
  );
  const shownRow = attempts.find(({ id }) => id === selectedId) ?? attempt;
  const isPending = shownRow.id === attempt.id;
  const earlier = useGetKnowledgeCheckAttemptQuery(shownRow.id, {
    skip: isPending,
  });
  const shownQuery = isPending ? pending : earlier;
  const shown = shownQuery.data;

  const pendingDraft =
    draft ?? (pending.data ? createDraft(pending.data.answers) : null);
  const shownDraft = isPending
    ? pendingDraft
    : shown
      ? createDecidedDraft(shown)
      : null;
  const answers = shown ? orderAnswers(shown.answers, check.data) : [];
  const score =
    shown && shownDraft ? getConfirmedScore(shown.answers, shownDraft) : null;
  const isPass = score ? isPreviewedPass(score, check.data) : false;
  const isLastAttempt =
    check.data !== undefined && attempt.attemptNumber >= check.data.maxAttempts;
  const blocker =
    pending.data && pendingDraft
      ? getReviewBlocker(pending.data.answers, pendingDraft)
      : REVIEW_BLOCKERS.VERDICTS;
  const isAiReviewed = shown ? hasAiVerdicts(shown.answers) : true;
  const flagged = shown && isAiReviewed ? countAiFlagged(shown.answers) : null;

  const updateDraft = (changes: Partial<ReviewDraft>) => {
    if (pendingDraft) {
      onDraftChange(attempt.id, { ...pendingDraft, ...changes });
    }
  };

  const handleVerdictChange = (field: number, verdict: ReviewVerdict) =>
    pendingDraft &&
    updateDraft({ verdicts: { ...pendingDraft.verdicts, [field]: verdict } });

  const handleFeedbackChange = (field: number, feedback: string) =>
    pendingDraft &&
    updateDraft({ feedback: { ...pendingDraft.feedback, [field]: feedback } });

  const handleSubmit = async () => {
    const request =
      pending.data && pendingDraft
        ? buildReviewRequest(pending.data, pendingDraft)
        : null;
    if (!request) {
      return;
    }
    try {
      const decided = await reviewAttempt(request).unwrap();
      notification.success({
        message: t(
          DECIDED_TOAST_KEYS[decided.state] ??
            "certification.admin.knowledgeCheckFlags.saved",
          { name },
        ),
      });
      onDecided(attempt.id);
    } catch (error) {
      if ((error as FetchBaseQueryError).status === 409) {
        notification.warning({
          message: t("certification.admin.knowledgeCheckFlags.alreadyDecided"),
        });
        onDecided(attempt.id);
        return;
      }
      notification.error({
        message: t("certification.admin.knowledgeCheckFlags.reviewError"),
      });
    }
  };

  let introKey = "certification.admin.knowledgeCheckFlags.introDecided";
  if (isPending) {
    introKey = isAiReviewed
      ? "certification.admin.knowledgeCheckFlags.intro"
      : "certification.admin.knowledgeCheckFlags.introNoAi";
  }

  let ctaKey = "certification.admin.knowledgeCheckFlags.requestRound";
  if (isPass) {
    ctaKey = "certification.admin.knowledgeCheckFlags.approve";
  } else if (isLastAttempt) {
    ctaKey = "certification.admin.knowledgeCheckFlags.finalFail";
  }

  const footer =
    isPending && pendingDraft && score ? (
      <div className="kc-modal-footer flex flex-col gap-300">
        <div className="flex flex-wrap items-end justify-between gap-300">
          <div className="flex flex-col">
            <span
              className={`body-s-medium ${isPass ? "kc-score-pass" : "kc-flagged"}`}
            >
              {t("certification.admin.knowledgeCheckFlags.score", {
                correct: score.correct,
                total: score.total,
                percent: score.percent,
              })}
            </span>

            <span id={hintId} className="kc-muted body-s-regular">
              {blocker
                ? t(
                    `certification.admin.knowledgeCheckFlags.blocker.${blocker}`,
                  )
                : t("certification.admin.knowledgeCheckFlags.scoreHint")}
            </span>
          </div>

          <BaseTooltip
            title={
              blocker
                ? t(
                    `certification.admin.knowledgeCheckFlags.blocker.${blocker}`,
                  )
                : undefined
            }
          >
            <BaseButton
              loading={isLoading}
              disabled={blocker !== null}
              ariaDescribedBy={hintId}
              onClick={handleSubmit}
            >
              {t(ctaKey)}
            </BaseButton>
          </BaseTooltip>
        </div>
      </div>
    ) : null;

  let answerList;
  if (shownQuery.isError) {
    answerList = (
      <p className="kc-muted body-s-regular" role="alert">
        {t("certification.admin.knowledgeCheckFlags.attemptError")}
      </p>
    );
  } else if (!shown || !shownDraft) {
    answerList = (
      <BaseSkeletonGroup label={t("labels.loading")}>
        <BaseSkeleton
          variant={SKELETON_VARIANTS.TEXT}
          lines={ANSWER_SKELETON_LINES}
        />
      </BaseSkeletonGroup>
    );
  } else {
    answerList = (
      <ol className="kc-answer-list flex flex-col gap-300">
        {answers.map(({ answer, position, title }) => (
          <AnswerReview
            key={answer.id}
            answer={answer}
            position={position}
            title={title}
            verdict={shownDraft.verdicts[answer.field] ?? null}
            feedback={shownDraft.feedback[answer.field] ?? ""}
            readOnly={!isPending}
            onVerdictChange={(verdict) =>
              handleVerdictChange(answer.field, verdict)
            }
            onFeedbackChange={(feedback) =>
              handleFeedbackChange(answer.field, feedback)
            }
          />
        ))}
      </ol>
    );
  }

  return (
    <BaseModal
      open={open}
      width={REVIEW_MODAL_WIDTH}
      onCancel={onClose}
      title={name}
      subtitle={subtitle}
      footer={footer}
    >
      <div className="flex flex-col gap-400">
        <div className="kc-attempt-row flex flex-wrap items-center justify-between gap-300">
          <div className="flex flex-wrap items-center gap-200">
            <div
              role="group"
              aria-label={t("certification.admin.knowledgeCheckFlags.attempts")}
              className="flex flex-wrap items-center gap-100"
            >
              {attempts.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`kc-attempt-chip body-xs-regular ${
                    item.id === shownRow.id ? "kc-attempt-chip-selected" : ""
                  }`}
                  aria-pressed={item.id === shownRow.id}
                  onClick={() => setSelectedId(item.id)}
                >
                  {t("certification.admin.knowledgeCheckFlags.attemptChip", {
                    number: item.attemptNumber,
                  })}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-400">
            {shown && !isAiReviewed && (
              <span className="kc-flagged body-s-regular">
                {t("certification.admin.knowledgeCheckFlags.aiNotReviewed")}
              </span>
            )}

            {flagged !== null && (
              <span className="kc-flagged body-s-regular">
                {flagged > 0
                  ? t("certification.admin.knowledgeCheckFlags.aiFlagged", {
                      count: flagged,
                      total: shown?.answers.length ?? 0,
                    })
                  : t(
                      "certification.admin.knowledgeCheckFlags.aiFlaggedNothing",
                    )}
              </span>
            )}

            <span className="kc-muted body-s-regular">
              {t("certification.admin.knowledgeCheckFlags.submitted", {
                date: formatShortCertificationDate(shownRow.submittedAt),
              })}
            </span>
          </div>
        </div>

        <p className="kc-muted body-s-regular">{t(introKey)}</p>

        {answerList}
      </div>
    </BaseModal>
  );
};

export default ReviewModalBody;
